import { STARTER_TEMPLATES, type TemplateDoc } from '@quickreport/domain';
import type { LocalDb } from './client';
import { saveTemplateVersion } from './templates';

/** A new lab starts with the starter templates; edits sync up like any other change. */
export async function seedStarterTemplates(db: LocalDb, orgId: string): Promise<void> {
  const have = await db.query<{ n: number }>(
    'select count(*)::int n from templates where org_id = $1',
    [orgId],
  );
  if ((have.rows[0]?.n ?? 0) > 0) return;
  await db.transaction(async (tx) => {
    for (const doc of Object.values(STARTER_TEMPLATES)) {
      await saveTemplateVersion(tx, orgId, null, doc);
    }
  });
}

export async function firstSite(db: LocalDb, orgId: string) {
  const r = await db.query<{
    id: string;
    name: string;
    code: string;
    workflow_level: 'simple' | 'standard' | 'nabl';
  }>(
    'select id, name, code, workflow_level from sites where org_id = $1 and deleted_at is null order by created_at limit 1',
    [orgId],
  );
  return r.rows[0] ?? null;
}

/** Names compared without case or spacing, so "TSH, Ultrasensitive" matches "tsh,  ultrasensitive". */
const same = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Starter templates this lab does not have yet, matched by name. A lab's own templates are never
 * touched: this only lists what could be added.
 */
export async function missingStarterTemplates(
  db: LocalDb,
  orgId: string,
): Promise<{ key: string; doc: TemplateDoc }[]> {
  const r = await db.query<{ name: string }>(
    'select name from templates where org_id = $1 and deleted_at is null',
    [orgId],
  );
  const have = new Set(r.rows.map((x) => same(x.name)));
  return Object.entries(STARTER_TEMPLATES)
    .filter(([, doc]) => !have.has(same(doc.name)))
    .map(([key, doc]) => ({ key, doc }));
}

/** Add the chosen starters as new templates of this lab. Nothing existing is changed. */
export async function addStarterTemplates(
  db: LocalDb,
  orgId: string,
  keys: string[],
): Promise<number> {
  const wanted = (await missingStarterTemplates(db, orgId)).filter((m) => keys.includes(m.key));
  await db.transaction(async (tx) => {
    for (const m of wanted) await saveTemplateVersion(tx, orgId, null, m.doc);
  });
  return wanted.length;
}
